import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { useApi } from '../hooks/useApi';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import ConfirmModal from './ConfirmModal';
import { CAP } from '../utils/permissions';
import { usePermissions, ReadOnlyNotice } from '../utils/PermissionContext';

const EditIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
        <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10" />
    </svg>
);

const DeleteIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
        <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
    </svg>
);

const VisibleIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 text-green-600">
        <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
    </svg>
);

const InvisibleIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 text-gray-400">
        <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 0 0 1.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.451 10.451 0 0 1 12 4.5c4.756 0 8.773 3.162 10.065 7.498a10.522 10.522 0 0 1-4.293 5.774M6.228 6.228 3 3m3.228 3.228 3.65 3.65m7.894 7.894L21 21m-3.228-3.228-3.65-3.65m0 0a3 3 0 1 0-4.243-4.243m4.242 4.242L9.88 9.88" />
    </svg>
);

const SortIcon = ({ direction }) => {
    if (!direction) return (
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-3 h-3 text-gray-400">
            <path strokeLinecap="round" strokeLinejoin="round" d="M3 7.5L7.5 3m0 0L12 7.5M7.5 3v13.5m9-7.5L12 16.5m0 0L7.5 12m4.5 4.5V3" />
        </svg>
    );
    return (
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-3 h-3">
            {direction === 'ascending' ? (
                <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 15.75l7.5-7.5 7.5 7.5" />
            ) : (
                <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
            )}
        </svg>
    );
};

const EmailTemplatesSkeleton = ({ t }) => {
    return (
        <div className="bg-white shadow rounded-lg overflow-x-auto animate-pulse">
            <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-primary text-white opacity-85">
                    <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider w-32">Days Out</th>
                        <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Subject</th>
                        <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider w-28">Enabled</th>
                        <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider w-32">Actions</th>
                    </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                    {[1, 2, 3, 4, 5].map((rowIdx) => (
                        <tr key={rowIdx}>
                            <td className="px-6 py-4 whitespace-nowrap">
                                <div className="h-4 bg-gray-200 rounded w-16"></div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                                <div className="h-4 bg-gray-200 rounded w-64"></div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                                <div className="h-6 w-6 bg-gray-200 rounded"></div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right">
                                <div className="flex justify-end gap-3">
                                    <div className="h-6 w-6 bg-gray-200 rounded"></div>
                                    <div className="h-6 w-6 bg-gray-200 rounded"></div>
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

const EmailTemplates = ({ t }) => {
    const { can } = usePermissions();
    const canWrite = can(CAP.ADMIN_WRITE);
    const { apiRequest } = useApi(localStorage.getItem('googleIdToken'), () => window.location.reload());

    const [data, setData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteId, setDeleteId] = useState(null); // template id pending delete confirmation

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [sortConfig, setSortConfig] = useState(null);

    useEffect(() => {
        fetchCollectionData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchCollectionData = async () => {
        setIsLoading(true);
        try {
            const result = await apiRequest("/api/email-templates", { method: 'GET' });
            const arrayData = Array.isArray(result) ? result : (result.data || []);
            setData(arrayData);
        } catch (error) {
            console.error("Error fetching data:", error);
            toast.error("Failed to load email templates.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleDelete = async (id) => {
        setIsDeleting(true);
        try {
            await apiRequest(`/api/email-templates?id=${id}`, { method: 'DELETE' });
            toast.success("Template deleted successfully.");
            fetchCollectionData();
        } catch (error) {
            console.error("Error deleting:", error);
            toast.error("Failed to delete item.");
        } finally {
            setIsDeleting(false);
        }
    };

    const handleEdit = (item) => {
        let subjectEN = item.subject || '';
        let subjectNL = '';
        if (item.subject && item.subject.includes(' / ')) {
            const parts = item.subject.split(' / ');
            subjectEN = parts[0];
            subjectNL = parts.slice(1).join(' / ');
        }

        let bodyNL = '';
        let bodyEN = '';
        const bodyText = item.body || '';

        const matchDouble = bodyText.match(/(?:<p>|<div>)<b>NL:<\/b>(?:<br\/>)?\s*([\s\S]*?)(?:<\/p>|<\/div>)\s*(?:<p>|<div>)<b>EN:<\/b>(?:<br\/>)?\s*([\s\S]*?)(?:<\/p>|<\/div>)\s*$/i);
        
        if (matchDouble) {
            bodyNL = matchDouble[1].trim();
            bodyEN = matchDouble[2].trim();
        } else {
            const nlMatch = bodyText.match(/<p><b>NL:<\/b>\s*(.*?)<\/p>/is);
            if (nlMatch) bodyNL = nlMatch[1];
            const enMatch = bodyText.match(/<p><b>EN:<\/b>\s*(.*?)<\/p>/is);
            if (enMatch) bodyEN = enMatch[1];
            if (!nlMatch && !enMatch) bodyEN = bodyText;
        }

        setEditingItem({
            ...item,
            subjectEN,
            subjectNL,
            bodyEN,
            bodyNL
        });
        setIsModalOpen(true);
    };

    const handleAddNew = () => {
        setEditingItem({ visible: true, subjectEN: '', subjectNL: '', bodyEN: '', bodyNL: '', daysOut: '' });
        setIsModalOpen(true);
    };

    const handleToggleVisibility = async (item) => {
        setIsSaving(true);
        const updatedItem = { ...item, visible: item.visible === false ? true : false };
        try {
            await apiRequest("/api/email-templates", {
                method: 'POST',
                body: JSON.stringify(updatedItem)
            });
            toast.success("Visibility updated!");
            fetchCollectionData();
        } catch (error) {
            console.error("Error updating visibility:", error);
            toast.error("Failed to update visibility.");
        } finally {
            setIsSaving(false);
        }
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setEditingItem(null);
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setIsSaving(true);
        try {
            const subject = (editingItem.subjectEN && editingItem.subjectNL)
                ? `${editingItem.subjectEN} / ${editingItem.subjectNL}`
                : editingItem.subjectEN || editingItem.subjectNL || '';

            const body = `<div><b>NL:</b><br/>${editingItem.bodyNL || ''}</div><div><b>EN:</b><br/>${editingItem.bodyEN || ''}</div>`;

            const payload = {
                ...editingItem,
                subject,
                body
            };
            delete payload.subjectEN;
            delete payload.subjectNL;
            delete payload.bodyEN;
            delete payload.bodyNL;

            await apiRequest("/api/email-templates", {
                method: 'POST',
                body: JSON.stringify(payload)
            });
            toast.success("Saved successfully!");
            closeModal();
            fetchCollectionData();
        } catch (error) {
            console.error("Error saving:", error);
            toast.error("Failed to save. Check console.");
        } finally {
            setIsSaving(false);
        }
    };

    const handleInputChange = (e, fieldKey, type) => {
        let value = e.target.value;
        if (type === 'number') value = parseInt(value, 10);
        setEditingItem(prev => ({ ...prev, [fieldKey]: value }));
    };

    const sortedData = React.useMemo(() => {
        let sortableItems = [...data];
        if (sortConfig !== null) {
            sortableItems.sort((a, b) => {
                let valA = a[sortConfig.key];
                let valB = b[sortConfig.key];

                if (typeof valA === 'string') valA = valA.toLowerCase();
                if (typeof valB === 'string') valB = valB.toLowerCase();

                if (valA < valB) return sortConfig.direction === 'ascending' ? -1 : 1;
                if (valA > valB) return sortConfig.direction === 'ascending' ? 1 : -1;
                return 0;
            });
        } else {
            // sort by daysOut by default descending
            sortableItems.sort((a, b) => b.daysOut - a.daysOut);
        }
        return sortableItems;
    }, [data, sortConfig]);

    const requestSort = (key) => {
        let direction = 'ascending';
        if (sortConfig && sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };

    return (
        <div className="p-4 sm:p-8">
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-3xl font-bold text-gray-800 flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 mr-3 bg-primary text-white rounded-full p-1.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 19v-8.93a2 2 0 01.89-1.664l7-4.666a2 2 0 012.22 0l7 4.666A2 2 0 0121 10.07V19M3 19a2 2 0 002 2h14a2 2 0 002-2M3 19l6.75-4.5M21 19l-6.75-4.5M3 10l6.75 4.5M21 10l-6.75 4.5m0 0l-1.14.76a2 2 0 01-2.22 0l-1.14-.76" />
                    </svg>
                    Email Templates
                </h1>
                {canWrite && <button onClick={handleAddNew} className="bg-primary text-white px-4 py-2 rounded hover:opacity-90 shadow flex items-center gap-2">
                    <span>+ Add New Template</span>
                </button>}
            </div>

            <ReadOnlyNotice t={t} />

            <div className="mb-4 text-gray-600">
                Manage email notifications based on the number of days until a workspace subscription expires.
                <br />
                Use <code>daysOut</code> positive for days before expiry, 0 for day of expiry, and negative for days after expiry.
                <br />
                Available placeholders: <code>{`{{customer_name}}, {{customer_domain}}, {{sku_name}}, {{renewal_date}}, {{days_left}}`}</code>
            </div>

            {isLoading ? (
                <EmailTemplatesSkeleton t={t} />
            ) : (
                <div className="bg-white shadow rounded-lg overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-primary text-white">
                            <tr>
                                <th onClick={() => requestSort('daysOut')} className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer transition-colors group">
                                    <div className="flex items-center gap-1">Days Out <SortIcon direction={sortConfig?.key === 'daysOut' ? sortConfig.direction : null} /></div>
                                </th>
                                <th onClick={() => requestSort('subject')} className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer transition-colors group">
                                    <div className="flex items-center gap-1">Subject <SortIcon direction={sortConfig?.key === 'subject' ? sortConfig.direction : null} /></div>
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer transition-colors group">
                                    <div className="flex items-center gap-1">Enabled</div>
                                </th>
                                <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {sortedData.map((item, index) => (
                                <tr key={item.id || index} className="hover:bg-gray-50">
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{item.daysOut}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.subject}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {/* Also the Enabled indicator, so it stays
                                            visible but inert without adminWrite. */}
                                        <button
                                            onClick={() => handleToggleVisibility(item)}
                                            disabled={!canWrite || isSaving}
                                            className={`p-1 rounded transition-colors flex items-center ${!canWrite ? 'cursor-default' : isSaving ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-100'}`}
                                        >
                                            {item.visible !== false ? <VisibleIcon /> : <InvisibleIcon />}
                                        </button>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                        <div className="flex justify-end gap-3">
                                            {canWrite && <>
                                            <button onClick={() => handleEdit(item)} disabled={isDeleting} className="text-indigo-600 hover:text-indigo-900 p-1 hover:bg-indigo-50 rounded transition-colors" title="Edit"><EditIcon /></button>
                                            <button onClick={() => setDeleteId(item.id)} disabled={isDeleting} className="text-red-600 hover:text-red-900 p-1 hover:bg-red-50 rounded transition-colors disabled:opacity-50" title="Delete"><DeleteIcon /></button>
                                            </>}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {data.length === 0 && (
                                <tr>
                                    <td colSpan={4} className="px-6 py-4 text-center text-gray-500">
                                        No templates found.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {isModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl p-6">
                        <h2 className="text-xl font-bold mb-4">{editingItem?.id ? "Edit Template" : "Add New Template"}</h2>
                        <form onSubmit={handleSave}>
                            <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Days Out (e.g. 30, 15, 0, -7)</label>
                                    <input type="number" required className="w-full border rounded p-2 focus:ring-2 focus:ring-blue-500 outline-none" disabled={isSaving} value={editingItem.daysOut ?? ''} onChange={(e) => handleInputChange(e, 'daysOut', 'number')} />
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Subject (NL)</label>
                                            <input type="text" required className="w-full border rounded p-2 focus:ring-2 focus:ring-blue-500 outline-none" disabled={isSaving} value={editingItem.subjectNL || ''} onChange={(e) => handleInputChange(e, 'subjectNL', 'text')} />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Body (NL) - HTML allowed</label>
                                            <div className="h-48 mb-10">
                                                <ReactQuill
                                                    theme="snow"
                                                    readOnly={isSaving}
                                                    value={editingItem.bodyNL || ''}
                                                    onChange={(value) => handleInputChange({ target: { value } }, 'bodyNL', 'textarea')}
                                                    className="h-full bg-white"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Subject (EN)</label>
                                            <input type="text" required className="w-full border rounded p-2 focus:ring-2 focus:ring-blue-500 outline-none" disabled={isSaving} value={editingItem.subjectEN || ''} onChange={(e) => handleInputChange(e, 'subjectEN', 'text')} />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Body (EN) - HTML allowed</label>
                                            <div className="h-48 mb-10">
                                                <ReactQuill
                                                    theme="snow"
                                                    readOnly={isSaving}
                                                    value={editingItem.bodyEN || ''}
                                                    onChange={(value) => handleInputChange({ target: { value } }, 'bodyEN', 'textarea')}
                                                    className="h-full bg-white"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="flex items-center mt-4 mb-2">
                                    <button
                                        type="button"
                                        onClick={() => handleInputChange({ target: { value: editingItem.visible === false ? true : false } }, 'visible', 'boolean')}
                                        disabled={isSaving}
                                        className={`p-1 rounded transition-colors flex items-center ${isSaving ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-100'}`}
                                    >
                                        {editingItem.visible !== false ? <VisibleIcon /> : <InvisibleIcon />}
                                    </button>
                                    <span className="ml-2 text-sm text-gray-700 cursor-pointer" onClick={() => !isSaving && handleInputChange({ target: { value: editingItem.visible === false ? true : false } }, 'visible', 'boolean')}>Enabled</span>
                                </div>
                            </div>
                            <div className="mt-6 flex justify-end gap-3">
                                <button type="button" onClick={closeModal} disabled={isSaving} className="px-4 py-2 border rounded text-gray-700 hover:bg-gray-100 disabled:opacity-50">{t?.cancel || 'Cancel'}</button>
                                <button type="submit" disabled={isSaving} className="px-4 py-2 bg-primary text-white rounded hover:opacity-90 disabled:opacity-50 flex items-center gap-2">
                                    {isSaving && <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>}
                                    {isSaving ? (t?.saving || 'Saving...') : (t?.save_changes || 'Save')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <ConfirmModal
                isOpen={!!deleteId}
                title={t?.delete || 'Delete'}
                message={t?.delete_item_confirmation || 'Are you sure you want to delete this item?'}
                confirmLabel={t?.delete || 'Delete'}
                cancelLabel={t?.cancel || 'Cancel'}
                tone="danger"
                busy={isDeleting}
                onCancel={() => setDeleteId(null)}
                onConfirm={async () => {
                    const id = deleteId;
                    await handleDelete(id);
                    setDeleteId(null);
                }}
            />
        </div>
    );
};

export default EmailTemplates;
